import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Download, EllipsisVertical, MonitorDown, Share, SquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/layout/Logo";
import { canInstall, device, isStandalone, onInstallChange, promptInstall } from "@/lib/pwa";

function Step({ n, icon: Icon, title, children }) {
  return (
    <li className="flex gap-3 items-start">
      <div className="relative shrink-0">
        <div className="h-14 w-14 rounded-2xl bg-sky-50 border border-sky-200 grid place-items-center text-sky-700"><Icon className="h-7 w-7" /></div>
        <span className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-neutral-900 text-white text-xs font-bold grid place-items-center">{n}</span>
      </div>
      <div className="pt-1"><div className="font-semibold">{title}</div><div className="text-sm text-muted-foreground">{children}</div></div>
    </li>
  );
}

function IphoneSteps({ safari, ios }) {
  return (
    <section className="surface p-5 sm:p-6" data-testid="install-iphone-steps">
      <h2 className="font-heading text-lg font-semibold mb-1">iPhone & iPad</h2>
      {ios && !safari && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3" data-testid="install-ios-open-safari">Open this page in Safari first. Other iPhone browsers can't add apps to the Home Screen.</p>}
      <ol className="space-y-4 mt-3">
        <Step n={1} icon={Share} title="Tap the Share button">The square with an arrow, at the bottom of Safari (top on iPad).</Step>
        <Step n={2} icon={SquarePlus} title='Tap "Add to Home Screen"'>Scroll down the share menu if you don't see it.</Step>
        <Step n={3} icon={CheckCircle2} title='Tap "Add"'>Then open Da Lil Shop from your Home Screen and tap "Turn on notifications".</Step>
      </ol>
    </section>
  );
}

function OneTap({ title, children, testId }) {
  const [ready, setReady] = useState(canInstall());
  useEffect(() => onInstallChange(() => setReady(canInstall())), []);
  return (
    <section className="surface p-5 sm:p-6" data-testid={testId}>
      <h2 className="font-heading text-lg font-semibold mb-3">{title}</h2>
      {ready ? (
        <Button onClick={promptInstall} className="h-12 rounded-xl text-base w-full sm:w-auto" data-testid="install-app-button"><Download className="h-5 w-5 mr-2" />Install app</Button>
      ) : <ol className="space-y-4">{children}</ol>}
    </section>
  );
}

export default function InstallPage() {
  const d = device();
  const installed = isStandalone();
  const iphone = <IphoneSteps key="ios" safari={d.safari} ios={d.ios} />;
  const android = (
    <OneTap key="android" title="Android" testId="install-android-steps">
      <Step n={1} icon={EllipsisVertical} title="Open the Chrome menu">The three dots at the top right.</Step>
      <Step n={2} icon={Download} title='Tap "Install app" or "Add to Home screen"'>Then tap Install.</Step>
    </OneTap>
  );
  const desktop = (
    <OneTap key="desktop" title="Windows & Mac computer" testId="install-desktop-steps">
      <Step n={1} icon={MonitorDown} title="Chrome or Edge">Click the install icon at the right end of the address bar, then Install.</Step>
      <Step n={2} icon={Share} title="Safari on Mac">File → Add to Dock.</Step>
    </OneTap>
  );
  const order = d.ios ? [iphone, android, desktop] : d.android ? [android, iphone, desktop] : [desktop, iphone, android];
  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:py-12">
      <div className="max-w-xl mx-auto">
        <div className="mb-6"><Logo /></div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="install-title">Install the app</h1>
        <p className="text-sm text-muted-foreground mt-2 mb-6">Put Da Lil Shop Finance on your phone or computer like a normal app, with its own icon and notifications.</p>
        {installed ? (
          <div className="surface p-6 flex items-center gap-3 text-emerald-800" data-testid="install-installed">
            <CheckCircle2 className="h-7 w-7 text-emerald-600" /><span className="text-lg font-semibold">App installed ✓</span>
          </div>
        ) : <div className="space-y-4" data-testid="install-steps">{order}</div>}
        <p className="mt-8 text-sm"><Link to="/" className="font-semibold text-sky-700 underline" data-testid="install-back-link">Back to the app</Link></p>
      </div>
    </div>
  );
}
